import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { formatMoney } from "@theme/money-formatting";

/**
 * <product-total-price-n>
 *
 * Custom-template-only, DISPLAY-ONLY line total for the main Product
 * Information price. When the quantity selector changes, it shows
 * `unit price × quantity` next to the status pill; at quantity 1 it shows the
 * native `<product-price>` (so sale / compare-at / variant re-render stay
 * intact). It never touches product/variant/cart/checkout prices — Shopify
 * still calculates the real order total.
 *
 * Approach:
 *  - unit price comes from `data-unit-price` (integer MINOR units, e.g. fils for
 *    KWD), never parsed from the formatted string;
 *  - the amount is formatted with Horizon's own `formatMoney` using the store's
 *    money format + active currency (so KWD 3-decimals, separators, symbol
 *    position and localization all follow the theme);
 *  - it reacts only to `ThemeEvents.quantitySelectorUpdate` originating from the
 *    quantity selector inside THIS section (never a cart-drawer line, never a
 *    document-wide change listener).
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} total - The computed line-total element.
 * @extends {Component<Refs>}
 */
class ProductTotalPriceN extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#onQuantityUpdate,
      {
        signal: this.#abortController.signal,
      },
    );
    // Resync when the page is restored from the browser's back/forward cache
    // (e.g. returning from Checkout via the Back button). On a bfcache restore
    // connectedCallback does NOT run again and no quantity event is dispatched,
    // but the browser restores the quantity input — so recompute from it.
    window.addEventListener("pageshow", this.#onPageShow, {
      signal: this.#abortController.signal,
    });
    // Reflect the initial quantity (e.g. when quantity rules force a minimum > 1).
    this.#update(this.#currentQuantity());
  }

  /**
   * Back/forward-cache restore handler — recompute from the restored quantity.
   */
  #onPageShow = () => {
    this.#update(this.#currentQuantity());
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Re-sync after Section Rendering / DOM morph (variant change, Theme Editor).
   */
  updatedCallback() {
    super.updatedCallback();
    this.#update(this.#currentQuantity());
  }

  /**
   * @param {Event} event
   */
  #onQuantityUpdate = (event) => {
    const detail = /** @type {{ quantity?: number, cartLine?: number }} */ (
      /** @type {CustomEvent} */ (event).detail
    );
    // Ignore cart-drawer line quantity changes — only the product-page selector.
    if (detail?.cartLine != null) return;
    // Only react to the quantity selector inside THIS product section.
    const section = this.closest(".shopify-section");
    if (
      !section ||
      !(event.target instanceof Node) ||
      !section.contains(event.target)
    )
      return;

    this.#update(this.#currentQuantity());
  };

  /**
   * Current purchase quantity from this section's product form (source of truth).
   * @returns {number}
   */
  #currentQuantity() {
    const section = this.closest(".shopify-section");
    const input = /** @type {HTMLInputElement | null} */ (
      section?.querySelector('product-form-component input[name="quantity"]') ??
        null
    );
    return this.#normalize(input?.value);
  }

  /**
   * @param {string | number | undefined | null} value
   * @returns {number}
   */
  #normalize(value) {
    const n = Math.floor(Number(value));
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  /**
   * Toggle between the native unit price (qty 1) and the computed total (qty > 1).
   * @param {number} quantity
   */
  /**
   * Toggle between the native unit price (qty 1)
   * and the computed total (qty > 1).
   *
   * @param {number} quantity
   */
  #update(quantity) {
    const total = this.refs.total;

    const unitEl = /** @type {HTMLElement | null} */ (
      this.querySelector(".product-total-price-n__unit")
    );

    const unitPrice = parseInt(this.dataset.unitPrice ?? "", 10);

    if (!total || !unitEl || !Number.isFinite(unitPrice)) return;

    if (quantity > 1) {
      const currency = (this.dataset.currency || "USD").toUpperCase();

      let format = this.dataset.moneyFormat || "{{amount}}";

      // Prevent KWD from displaying 4 decimal places: 9.0000 KD.
      if (currency === "KWD") {
        format = format.replace(/(\{\{\s*amount(?:_[a-z_]+)?\s*\}\})0/g, "$1");
      }

      /*
       * Shopify Liquid price:
       * 3 KWD = 300
       *
       * Horizon KWD minor units:
       * 3 KWD = 3000 fils
       */
      const currencyMultiplier = currency === "KWD" ? 10 : 1;

      const totalInMinorUnits = unitPrice * quantity * currencyMultiplier;

      total.textContent = formatMoney(totalInMinorUnits, format, currency);

      total.hidden = false;
      unitEl.hidden = true;
    } else {
      total.textContent = "";
      total.hidden = true;
      unitEl.hidden = false;
    }
  }
}

if (!customElements.get("product-total-price-n")) {
  customElements.define("product-total-price-n", ProductTotalPriceN);
}
